/**
 * S2: sign in, sign up, and the two states that go wrong.
 *
 * This screen carries more brand weight than any other and contains almost no
 * product. Three seconds on it decides whether a person believes this is a
 * commercial cloud company, so it is one column, one mark, one heading, one
 * ink block, and nothing else. No illustration, no dot grid, no gradient.
 *
 * THE OAUTH DENIED STATE IS THE TONE REFERENCE FOR THE WHOLE PRODUCT.
 * "You didn't finish signing in with GitHub. Nothing has changed on your
 * account." No blame, no "you cancelled", and the second sentence is there
 * because the actual fear at that moment is that something happened. The
 * primary action does not change: the way out is the way in.
 *
 * States, every one of them deep linkable, per screen spec 1.3a:
 *   /sign-in                      resting
 *   /sign-in?state=signing-in     the GitHub button working
 *   /sign-in?state=oauth-denied   came back from GitHub without finishing
 *   /sign-in?state=unverified     signed up, email link not followed yet
 *   /sign-up                      the same screen, three strings different
 *
 * Signing up routes to /new and never to /projects. A new account with no
 * projects must not meet an empty projects list on its first screen: that is
 * the one moment where there is exactly one thing to do, so the product should
 * just do it.
 */

import { useEffect, useRef, useState, type FormEvent } from 'react';
import { Link, Navigate, useLocation, useNavigate } from 'react-router';

import { Button } from '../../components/primitives/Button';
import { Field } from '../../components/primitives/Field';
import { HeroBackdrop } from '../../components/patterns/HeroBackdrop';
import { Notice } from '../../components/patterns/Notice';
import { Skeleton } from '../../components/patterns/Skeleton';
import { GitHubMark } from '../../components/brand/GitHubMark';
import { Wordmark } from '../../components/brand/Wordmark';

import { setToken, useSessionStore } from '../../lib/auth/session';
import { useScreenState } from '../../lib/hooks/use-screen-state';
import { apiFetch, ApiError } from '../../lib/http';
import { t } from '../../lib/i18n/t';
import { useDemoStore } from '../../lib/mock/store';
import { captureEvent } from '../../lib/analytics/posthog';
import { useFormTracker } from '../../lib/analytics/use-form-tracker';
import { SSO_STATE_KEY } from './sso-state';

import '../../styles/screens/auth.css';
import type { Plan } from '../../lib/types';

// The demo apparatus (scenarios, the clock, the deep-linked ?state= screens)
// only makes sense against the mock backend. A real deployment signs in
// through Loop only, per this phase's admin/coach-only gate (LoopSignIn
// below) - see apps/cloud/CLAUDE.md's identity section and
// routes/sso.ts on the cloud-api side.
const USE_MOCK = import.meta.env.VITE_API_MODE === 'mock';
const LOOP_WEB_URL = (import.meta.env.VITE_LOOP_WEB_URL as string | undefined) ?? 'http://localhost:3000';

function startLoopSso() {
  captureEvent('loop_sso_initiated');
  const state = crypto.randomUUID();
  sessionStorage.setItem(SSO_STATE_KEY, state);
  const url = new URL('/app/cloud-sso', LOOP_WEB_URL);
  url.searchParams.set('state', state);
  url.searchParams.set('return_to', window.location.origin);
  window.location.href = url.toString();
}

interface SigninResponse {
  account: { id: string; email: string; display_name: string | null; mode: 'simple' | 'advanced'; language?: 'en' | 'th' | null; is_admin: boolean; plan_id?: Plan; plan_expires_at?: string | null; created_at: string };
  token: string;
}

/**
 * Password sign-in for a God View invite (F21) that has already claimed
 * its account. LOCAL_AUTH_ENABLED stays "false" server-side, but
 * routes/auth.ts's signin gate carves out an exception for
 * `auth_provider: "invite"` accounts specifically, so this form has
 * somewhere to submit even though local SIGNUP is still fully closed. Not
 * shown on /sign-up - there is no local signup path in this phase.
 */
function PasswordSignIn() {
  const navigate = useNavigate();
  const setAccount = useSessionStore((s) => s.setAccount);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const formTracker = useFormTracker({
    formId: 'password-signin',
    formName: 'Password Sign In',
    totalFields: 2,
  });

  function submit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    formTracker.trackSubmit();
    apiFetch<SigninResponse>('/api/v1/auth/signin', {
      method: 'POST',
      body: { email, password },
      unauthenticated: true,
    })
      .then((res) => {
        formTracker.trackSubmitSuccess();
        captureEvent('password_sign_in_success', { isAdmin: res.account.is_admin });
        setToken(res.token);
        setAccount({
          id: res.account.id,
          name: res.account.display_name ?? res.account.email.split('@')[0] ?? res.account.email,
          email: res.account.email,
          plan: res.account.plan_id ?? 'basic',
          planExpiresAt: res.account.plan_expires_at ?? null,
          detailLevel: res.account.mode,
          language: res.account.language ?? null,
          createdAt: res.account.created_at,
          githubHandle: '',
          isAdmin: res.account.is_admin,
          // Real value arrives from getAccount() on shell mount; default off until then.
          tenantStagingEnabled: false,
          teamsEnabled: false,
          svgitEnabled: false,
        });
        navigate('/projects', { replace: true });
      })
      .catch((err) => {
        const errorMsg = err instanceof ApiError ? err.message : t('Something went wrong. Try again in a moment.');
        const errorCode = err instanceof ApiError ? err.code : 'unknown';
        formTracker.trackSubmitError(errorCode, errorMsg);
        captureEvent('password_sign_in_failed', { error_code: errorCode, error_message: errorMsg });
        setError(errorMsg);
      })
      .finally(() => setBusy(false));
  }

  return (
    <form className="auth__wide" onSubmit={submit}>
      {/* autoComplete="username", not "email". Both offer the address, but
          only "username" tells a password manager that THIS is the field
          the password below belongs to, which is what makes the saved
          credential come back as a pair instead of the person having to
          type their address in every time. `name` matters for the same
          reason: managers key on it when the form has no id. */}
      <Field
        label={t('Email')}
        type="email"
        name="username"
        value={email}
        onFocus={() => formTracker.onFieldFocus('email')}
        onBlur={() => formTracker.onFieldBlur('email')}
        onChange={(e) => {
          setEmail(e.target.value);
          formTracker.onFieldChange('email', e.target.value);
        }}
        autoComplete="username"
        required
      />
      <Field
        label={t('Password')}
        type="password"
        name="current-password"
        value={password}
        onFocus={() => formTracker.onFieldFocus('password')}
        onBlur={() => formTracker.onFieldBlur('password')}
        onChange={(e) => {
          setPassword(e.target.value);
          formTracker.onFieldChange('password', e.target.value);
        }}
        autoComplete="current-password"
        error={error ?? undefined}
        required
      />
      <Button type="submit" variant="outline" loading={busy}>
        {t('Sign in with password')}
      </Button>
    </form>
  );
}

/**
 * The real sign-in screen. Local email/password SIGNUP is off server-side
 * for this phase (LOCAL_AUTH_ENABLED=false on cloud-api) - Loop SSO is the
 * only way to create an account, gated to Loop's ADMIN/COACH roles on both
 * sides of the exchange (cloud-api's routes/sso.ts, loop-api's
 * cloud-sso.controller.ts). A God View invite (F21) is the one other way
 * in: it creates an account directly, and PasswordSignIn above is how that
 * person returns after their first session expires.
 */
function LoopSignIn({ isSignUp }: { isSignUp: boolean }) {
  return (
    <div className="auth">
      <HeroBackdrop reach="tight" />
      <div className="auth__lockup">
        <Wordmark size={32} />
      </div>
      <div className="auth__card">
        <h1 className="auth__title">
          {isSignUp ? t('Create your SV Cloud account') : t('Sign in to SV Cloud')}
        </h1>
        <div className="auth__wide">
          <Button variant="solid" onClick={startLoopSso}>
            {t('Continue with Loop')}
          </Button>
        </div>
        <p className="auth__legal">
          {t(
            "SV Cloud is open to Loop admins and coaches right now. By continuing you agree to our terms of service and our privacy notice.",
          )}{' '}
          <LegalLinks />
        </p>
        {!isSignUp && <PasswordSignIn />}
      </div>
    </div>
  );
}

const STATES = ['resting', 'signing-in', 'oauth-denied', 'unverified'] as const;
type State = (typeof STATES)[number];

/** How long the round trip to GitHub takes, before the demo speed divides it. */
const OAUTH_MS = 4_000;

/** Screen spec S2: the resend control is inert for thirty seconds. */
const RESEND_COOLDOWN_S = 30;

/* ------------------------------------------------------- unverified state */

function Unverified({ email }: { email: string }) {
  const [left, setLeft] = useState(0);
  const timer = useRef<number | undefined>(undefined);

  useEffect(() => () => window.clearInterval(timer.current), []);

  function resend() {
    setLeft(RESEND_COOLDOWN_S);
    window.clearInterval(timer.current);
    timer.current = window.setInterval(() => {
      setLeft((n) => {
        if (n <= 1) window.clearInterval(timer.current);
        return Math.max(0, n - 1);
      });
    }, 1000);
  }

  return (
    <div className="auth__card">
      <h1 className="auth__title">{t('Check your email to finish setting up.')}</h1>
      <p className="auth__say">{t('We sent a link to {email}.', { email })}</p>
      <p className="auth__say auth__say--quiet">
        {t('The link works once and it expires in an hour. You can close this page.')}
      </p>

      <div className="auth__wide">
        <Button variant="solid" onClick={resend} disabled={left > 0}>
          {/* The cooldown counts down through 1, so one second gets its own
              key rather than reading "Resend in 1 seconds" on the last tick. */}
          {left > 1
            ? t('Resend in {n} seconds', { n: String(left) })
            : left === 1
              ? t('Resend in 1 second')
              : t('Send the link again')}
        </Button>
      </div>

      <p className="auth__foot">
        <Link className="link-inline" to="/sign-in">
          {t('Use a different email address')}
        </Link>
      </p>
    </div>
  );
}

/* ---------------------------------------------------------------- screen */

export default function SignIn() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const account = useDemoStore((s) => s.account);
  const speed = useDemoStore((s) => s.prefs.speed);
  const { state, setState } = useScreenState<State>(STATES, 'resting');

  const isSignUp = pathname === '/sign-up';

  /**
   * True only when the working state was entered by pressing the button here.
   *
   * The round trip is compressed by the same speed control every other
   * asynchronous thing in the prototype reads, so nobody waits four seconds in
   * front of an audience. But a state that walks off the screen on its own
   * cannot be reviewed, and ?state=signing-in exists to be looked at, so a
   * deep link holds the state still and only a real press starts the clock.
   */
  const pressed = useRef(false);

  useEffect(() => {
    if (state !== 'signing-in' || !pressed.current) return;
    const ms = speed === 'instant' ? 0 : OAUTH_MS / speed;
    const id = window.setTimeout(() => navigate(isSignUp ? '/new' : '/projects'), ms);
    return () => window.clearTimeout(id);
  }, [state, speed, isSignUp, navigate]);

  const sessionStatus = useSessionStore((s) => s.status);

  if (!USE_MOCK) {
    if (sessionStatus === 'signed-in') {
      return <Navigate to="/projects" replace />;
    }
    if (sessionStatus === 'loading') {
      return (
        <div className="auth">
          <HeroBackdrop reach="tight" />
          <div className="auth__lockup">
            <Wordmark size={32} />
          </div>
          <div className="auth__card">
            <h1 className="auth__title">{t('Sign in to SV Cloud')}</h1>
            <Skeleton label={t('Checking your session')} />
          </div>
        </div>
      );
    }
    return <LoopSignIn isSignUp={isSignUp} />;
  }

  if (state === 'unverified') {
    return (
      <div className="auth">
        <HeroBackdrop reach="tight" />
        <div className="auth__lockup">
          <Wordmark size={32} />
        </div>
        <Unverified email={account.email} />
      </div>
    );
  }

  const working = state === 'signing-in';

  return (
    <div className="auth">
      <HeroBackdrop reach="tight" />

      <div className="auth__lockup">
        <Wordmark size={32} />
      </div>

      <div className="auth__card">
        <h1 className="auth__title">
          {isSignUp ? t('Create your SV Cloud account') : t('Sign in to SV Cloud')}
        </h1>

        {state === 'oauth-denied' ? (
          <Notice tone="attention">
            <p>
              {t(
                "You didn't finish signing in with GitHub. Nothing has changed on your account.",
              )}
            </p>
          </Notice>
        ) : null}

        <div className="auth__wide">
          <Button
            variant="solid"
            loading={working}
            onClick={() => {
              pressed.current = true;
              setState('signing-in');
            }}
          >
            <span className="auth__gh-label">
              {working ? null : <GitHubMark />}
              {working ? t('Opening GitHub') : t('Continue with GitHub')}
            </span>
          </Button>
        </div>

        <div className="auth__or">
          <span className="auth__or-word">{t('or')}</span>
        </div>

        <form
          className="auth__form"
          onSubmit={(event) => {
            event.preventDefault();
            navigate(isSignUp ? '/sign-up?state=unverified' : '/projects');
          }}
        >
          <Field
            label={t('Email address')}
            type="email"
            name="username"
            autoComplete="username"
            placeholder="you@example.com"
          />
          <Field
            label={t('Password')}
            type="password"
            name="password"
            autoComplete={isSignUp ? 'new-password' : 'current-password'}
          />
          <div className="auth__wide">
            <Button variant="outline" type="submit" disabled={working}>
              {isSignUp ? t('Create account') : t('Sign in')}
            </Button>
          </div>
        </form>

        <p className="auth__foot">
          {isSignUp ? (
            <>
              {t('Already have an account?')}{' '}
              <Link className="link-inline" to="/sign-in">
                {t('Sign in')}
              </Link>
            </>
          ) : (
            <>
              {t('New here?')}{' '}
              <Link className="link-inline" to="/sign-up">
                {t('Create an account')}
              </Link>
            </>
          )}
        </p>

        <p className="auth__legal">
          {t('By continuing you agree to our terms of service and our privacy notice.')} <LegalLinks />
        </p>
      </div>

      <p className="auth__aside">
        {t('Not ready for an account?')}{' '}
        <Link className="link-inline" to="/waitlist">
          {t('Join the waitlist')}
        </Link>
      </p>
    </div>
  );
}

/** The two documents the sign in sentence refers to, as real links. */
function LegalLinks() {
  return (
    <>
      <Link className="link-inline" to="/terms">
        {t('Terms of service')}
      </Link>
      {' · '}
      <Link className="link-inline" to="/privacy">
        {t('Privacy policy')}
      </Link>
    </>
  );
}
